import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Calendar,
  CheckCircle2,
  AlertTriangle,
  Clock3,
  ShieldX,
  RefreshCw,
  LinkIcon,
  Activity,
} from "lucide-react";

/**
 * v2 PR-2C — Connected calendars card.
 *
 * Lives on the Account Profile page above ThemeCard. When the user has not
 * connected anything we render an empty state with a single connect button;
 * when there's an active connection we render its sync stats and a disconnect
 * action. Designed to be small (60 vertical lines collapsed) so the profile
 * page stays scannable.
 */
export default function CalendarConnectCard() {
  // v2 PR-2C.1 — the empty-state needs ONE bit: are Google OAuth credentials
  // configured anywhere (vault or env)? We deliberately use the host-safe
  // `/calendar/google/is-configured` endpoint here (returns just `{configured}`)
  // rather than the super_admin-flavoured `/google/status` which leaks the
  // credential SOURCE (vault vs env) and the redirect_uri — both of which
  // are platform-admin info that a plain host has no business reading.
  const { effectiveOrgId } = useAuth();
  // v2 PR-2C.1-followup — calendar sync is per-org-per-user. The Connect
  // button works when the caller has an *effective* org_id, which covers
  // BOTH:
  //   • normal users (own_org_id == effective_org_id)
  //   • super_admin impersonating an org (own=null, effective=impersonated)
  // A bare super_admin (no impersonation) gets effective_org_id=null and
  // sees the dedicated empty-state below.
  const hasOrg = !!effectiveOrgId;
  const [status, setStatus] = useState(null);   // {configured: bool}
  const [conn, setConn] = useState(null);       // live connection or null
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: rows }] = await Promise.all([
        api.get("/calendar/google/is-configured"),
        api.get("/calendar/connections"),
      ]);
      setStatus(s);
      const active = (rows || []).find(
        (r) => r.provider === "google" && !r.disconnected_at,
      );
      setConn(active || null);
    } catch (e) {
      console.warn("calendar load failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // After the OAuth roundtrip Google bounces back to
  // /account/profile?calendar_connected=google — show a toast + reload.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("calendar_connected") === "google") {
      toast.success("Google Calendar connected — first sync within 10 minutes.");
      window.history.replaceState({}, "", "/account/profile");
      load();
    }
  }, [load]);

  const connect = async () => {
    setWorking(true);
    try {
      const { data } = await api.get("/calendar/google/oauth-start");
      window.location.href = data.authorize_url;
    } catch (e) {
      // v2 PR-2C.1-followup — the backend uses structured detail bodies of the
      // shape `{error: "<code>", message: "<human>"}`. Surface the message
      // verbatim where we can, fall through to `formatApiError` (now
      // dict-shape-aware) for anything we don't have a tailored copy for.
      const det = e?.response?.data?.detail;
      if (det?.error === "google_not_configured") {
        toast.error("Google OAuth is not configured on this deployment.");
      } else if (det?.error === "no_org") {
        // super_admin (or any user with no org_id) hits this — calendar sync
        // is per-user-per-org. We render this state in EmptyState below too.
        toast.error("Calendar sync is per-organisation. Log in as your org's admin or host to connect a calendar.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setWorking(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(
      "Disconnect Google Calendar?\n\n" +
      "EntryBook will stop auto-creating pre-registrations from your meetings. " +
      "Existing pre-registrations are left untouched.",
    )) return;
    setWorking(true);
    try {
      await api.post("/calendar/google/disconnect");
      toast.success("Disconnected from Google Calendar.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWorking(false); }
  };

  return (
    <div className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-2xl p-5"
         data-testid="calendar-connect-card">
      <div className="inline-flex items-center gap-2 mb-3">
        <Calendar className="w-4 h-4 text-orange-500" />
        <h2 className="font-semibold text-navy-900 dark:text-white">Connected calendars</h2>
      </div>

      {loading ? (
        <div className="text-xs text-navy-400">Loading…</div>
      ) : conn ? (
        <ConnectedState conn={conn} working={working} onDisconnect={disconnect} onRefresh={load} />
      ) : (
        <EmptyState
          status={status}
          hasOrg={hasOrg}
          working={working}
          onConnect={connect}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------

function EmptyState({ status, hasOrg, working, onConnect }) {
  const configured = status?.configured;
  // v2 PR-2C.1-followup — three reasons the Connect button might be unusable:
  //   1. The platform has no Google OAuth credentials at all (`!configured`).
  //   2. The CALLER has no org_id (super_admin) — calendar sync is per-org.
  //   3. Both — `!configured` wins because it's the actionable blocker.
  // `disabled` covers all three; the helper copy below explains which path.
  const disabled = working || !configured || !hasOrg;
  let tooltip;
  if (!configured) {
    tooltip = "Google OAuth not configured — ask your platform admin to add Google OAuth credentials in Super Admin → Integrations → Google OAuth.";
  } else if (!hasOrg) {
    tooltip = "Calendar sync is per-organisation. Log in as your org's admin or host to connect a calendar.";
  } else {
    tooltip = "Click to start the Google OAuth flow.";
  }
  return (
    <div data-testid="calendar-empty-state">
      <p className="text-sm text-navy-500 dark:text-navy-300 mb-3 leading-relaxed">
        Connect your work calendar to auto-create pre-registrations from your meetings.
        EntryBook scans for meetings with <strong>external attendees</strong> at one of
        your org's sites, then mints visitor passes immediately — no copy-paste required.
        Internal-only meetings are skipped.
      </p>
      <Button
        onClick={onConnect}
        disabled={disabled}
        title={tooltip}
        className={`${!disabled ? "bg-orange-500 hover:bg-orange-600 text-white" : "bg-navy-200 text-navy-500 cursor-not-allowed"}`}
        data-testid="calendar-connect-google-btn"
      >
        <Calendar className="w-4 h-4 mr-1.5" />
        Connect Google Calendar
      </Button>
      {!configured && (
        <p className="mt-2 text-[11px] text-navy-400" data-testid="calendar-not-configured">
          Google OAuth not configured on this deployment. Ask your platform admin to add credentials in
          <strong className="mx-1">Super Admin → Integrations → Google OAuth</strong>.
        </p>
      )}
      {configured && !hasOrg && (
        <p className="mt-2 text-[11px] text-navy-400" data-testid="calendar-no-org">
          Calendar sync is per-organisation, but your account isn't bound to one
          (super_admin accounts span all orgs). Start <strong>impersonating an org</strong>
          from Super Admin → Organisations to connect a calendar on that org's behalf.
        </p>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------

const STATUS_STYLES = {
  ok: { icon: CheckCircle2, label: "Synced", colour: "bg-success-50 text-success-700 border-success-200" },
  auth_failed: { icon: ShieldX, label: "Auth failed", colour: "bg-danger-50 text-danger-700 border-danger-200" },
  rate_limited: { icon: Clock3, label: "Rate limited", colour: "bg-warning-50 text-warning-700 border-warning-200" },
  error: { icon: AlertTriangle, label: "Error", colour: "bg-danger-50 text-danger-700 border-danger-200" },
  null: { icon: RefreshCw, label: "Awaiting first sync", colour: "bg-navy-50 text-navy-700 border-navy-200" },
};

function ConnectedState({ conn, working, onDisconnect, onRefresh }) {
  const { formatDate } = useLocale();
  const key = conn.last_sync_status || "null";
  const style = STATUS_STYLES[key] || STATUS_STYLES.null;
  const StatusIcon = style.icon;
  const lastSync = conn.last_sync_at ? new Date(conn.last_sync_at) : null;
  const relSync = lastSync ? relativeTime(lastSync, formatDate) : "never";

  return (
    <div data-testid="calendar-connected-state">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <div className="text-sm text-navy-700 dark:text-navy-100 inline-flex items-center gap-2">
            <LinkIcon className="w-3.5 h-3.5 text-navy-400" />
            Connected as <strong data-testid="calendar-account-email">{conn.external_account_email || "—"}</strong>
          </div>
          <div className="mt-1 inline-flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${style.colour}`}
                  data-testid="calendar-sync-status-pill">
              <StatusIcon className="w-3 h-3" /> {style.label}
            </span>
            <span className="text-[11px] text-navy-400">· last sync {relSync}</span>
          </div>
          {conn.last_sync_error && (
            <p className="mt-1 text-[11px] text-danger-700 font-mono break-all">{conn.last_sync_error}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onRefresh} disabled={working}
                  data-testid="calendar-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh status
          </Button>
          <Button size="sm" variant="ghost"
                  className="text-danger-600 hover:bg-danger-50"
                  onClick={onDisconnect} disabled={working}
                  data-testid="calendar-disconnect-btn">
            Disconnect
          </Button>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <dt className="eb-overline mb-1">Events processed</dt>
          <dd className="text-navy-700 dark:text-navy-100 inline-flex items-center gap-1">
            <Activity className="w-3 h-3 opacity-50" /> {conn.events_processed_count || 0}
          </dd>
        </div>
        <div>
          <dt className="eb-overline mb-1">Pre-regs created</dt>
          <dd className="text-navy-700 dark:text-navy-100">{conn.pre_regs_created_count || 0}</dd>
        </div>
        <div>
          <dt className="eb-overline mb-1">Connected from</dt>
          <dd className="text-navy-700 dark:text-navy-100 font-mono">{conn.connected_from_ip_subnet || "—"}</dd>
        </div>
        <div>
          <dt className="eb-overline mb-1">Scopes</dt>
          <dd className="text-navy-700 dark:text-navy-100 text-[10px] font-mono break-all">
            {(conn.scopes || []).map((s) => s.split("/").pop()).join(", ")}
          </dd>
        </div>
      </dl>

      <p className="mt-3 text-[11px] text-navy-400 dark:text-navy-300 leading-relaxed">
        Pre-registrations are auto-created for meetings with external attendees at one of your org's sites.
        Internal-only meetings are skipped. Sync runs every 10 minutes.
      </p>
    </div>
  );
}

function relativeTime(d, formatDate) {
  // Phase B2 — caller threads `formatDate` from useLocale() because hooks
  // can't be called from a module-level helper.
  try {
    const diff = (d.getTime() - Date.now()) / 1000;
    const abs = Math.abs(diff);
    const past = diff < 0;
    const fmt = (n, u) => `${Math.floor(n)}${u} ${past ? "ago" : "from now"}`;
    if (abs < 60) return "just now";
    if (abs < 3600) return fmt(abs / 60, "m");
    if (abs < 86400) return fmt(abs / 3600, "h");
    if (abs < 86400 * 30) return fmt(abs / 86400, "d");
    return formatDate ? formatDate(d) : d.toISOString().slice(0, 10);
  } catch { return "—"; }
}
